import React, { useState, useEffect, useMemo } from 'react';
import { colorTextStatusError } from '@cloudscape-design/design-tokens';
import {
  Container,
  FormField,
  Textarea,
  Button,
  SpaceBetween,
  FileUpload,
  Box,
  Header,
  Alert,
} from '@cloudscape-design/components';
import moment from 'moment';
import { MarkCompleteModal } from '../modals/MarkCompleteModal';
import { MarkInProgressModal } from '../modals/MarkInProgressModal';
import { useMediaUpload } from 'src/hooks/useMediaUpload';
import { useFormValidation } from 'src/hooks/useFormValidation';
import { FormValidator, ValidationRules } from 'src/utils/FormValidator';
import ImageGallery, { ImageGalleryItem } from '../ImageGallery/ImageGallery';
import UploadProgressIndicator from '../UploadProgressIndicator';
import ApiStatusFlashBar from '../ApiStatusFlashBar';
import { EntityType } from 'src/contstant/commonLabels';
import {
  filterTaskPerformanceComments,
  isAssigneeChangeComment,
} from 'src/utils/commentUtils';
import { TaskStatus } from 'src/contstant/taskStatusColors';
import { useUserDetails } from 'src/providers/UserProvider';
import { Task } from 'src/types/helios/Task';

interface TaskPerformanceFormProps {
  onSave: (formData: TaskPerformanceFormData) => void;
  onStatusChange: (
    status: 'in-progress' | 'completed',
    comment?: string,
    formData?: TaskPerformanceFormData,
  ) => void;
  auditorObservation?: string;
  isLoading?: boolean;
  taskStatus?: string;
  task: Task;
}

export interface TaskPerformanceFormData {
  actionPerformed: string;
  comments: string;
  mediaIdsMap: Record<string, string[]>;
  dueDate?: string;
}

export const TaskPerformanceForm: React.FC<TaskPerformanceFormProps> = ({
  onSave,
  onStatusChange,
  auditorObservation,
  isLoading = false,
  taskStatus = '',
  task,
}) => {
  const initialActionPerformed = task.actionPerformed;
  const initialComments = task.comments;
  const existingMediaIds = task?.taskMediaIdsMap ?? {};
  const responseMedia = task.mediaResponses?.responseMedia;
  const initialDueDate = task.dueDate;
  const { permissions, user, userPreferences } = useUserDetails();
  const isReadOnlyMode = (() => {
    // Task is read-only if user doesn't have perform task permission
    if (!permissions.has('PerformTask')) {
      return true;
    }

    // Task is read-only if completed or cancelled
    if (
      task.taskStatus === TaskStatus.COMPLETED ||
      task.taskStatus === TaskStatus.CANCELLED
    ) {
      return true;
    }
    if (!task.assigneeLogin) {
      return false;
    }
    // Task is read-only if assigned to someone else (case-insensitive — logins are case-insensitive)
    return task.assigneeLogin?.toLowerCase() !== user.userId?.toLowerCase();
  })();
  const [formData, setFormData] = useState<TaskPerformanceFormData>({
    actionPerformed: initialActionPerformed || '',
    comments: filterTaskPerformanceComments(initialComments || ''),
    mediaIdsMap: existingMediaIds,
    dueDate: (() => {
      if (!initialDueDate) return '';
      try {
        return moment.utc(initialDueDate).format('YYYY-MM-DD');
      } catch {
        return '';
      }
    })(),
  });

  const [fileItems, setFileItems] = useState<ImageGalleryItem[]>([]);

  // Whether form fields should be disabled based on task status
  const isFieldDisabled =
    isReadOnlyMode ||
    taskStatus === TaskStatus.ASSIGNED ||
    taskStatus === TaskStatus.CANCELLED ||
    taskStatus === TaskStatus.COMPLETED;

  // Validation setup - memoize validator to ensure stability
  const validator = useMemo(() => {
    const validatorInstance = new FormValidator();

    if (!isFieldDisabled) {
      validatorInstance.addRule(
        'actionPerformed',
        ValidationRules.required,
        'Action Performed is required',
      );

      // Add due date validation with dynamic range
      validatorInstance.addRule(
        'dueDate',
        (value: string) => {
          if (!value) return true; // Due date is optional

          // For OVERDUE tasks: only allow if due date hasn't changed
          if (taskStatus === TaskStatus.OVERDUE) {
            // Normalize to YYYY-MM-DD in UTC for same-day comparison
            const normalizedValue = moment.utc(value).format('YYYY-MM-DD');
            const normalizedInitialDate = initialDueDate
              ? moment.utc(initialDueDate).format('YYYY-MM-DD')
              : '';

            // Allow unchanged due date (enables completing OVERDUE tasks)
            if (normalizedValue === normalizedInitialDate) {
              return true;
            }
            // Reject any attempt to change due date for OVERDUE tasks
            return false;
          }

          // Normal validation for non-OVERDUE tasks
          const selectedDate = moment.utc(value).startOf('day');
          const today = moment.utc().startOf('day');
          const existingDueDate = initialDueDate
            ? moment.utc(initialDueDate).startOf('day')
            : today;

          // Base date is the later of today or existing due date
          const baseDate = existingDueDate.isAfter(today) ? existingDueDate : today;
          const maxDate = baseDate.clone().add(10, 'days');

          return (
            selectedDate.isSameOrAfter(baseDate) && selectedDate.isSameOrBefore(maxDate)
          );
        },
        'Due date must be within 10 days from the current due date or today, whichever is later',
      );
    }

    return validatorInstance;
  }, [isReadOnlyMode, taskStatus, initialDueDate]);

  const { fieldValidationError, validate, errorALLMessage } = useFormValidation(
    formData,
    validator,
  );

  const {
    isUploading,
    uploadProgress,
    uploadfiles,
    deleteMedia,
    uploadMediaApiStatus,
    clearMediaAllApiStatus,
    deleteMediaApiStatus,
  } = useMediaUpload();

  // Update form data when task data changes (including due date changes from TaskDetails)
  useEffect(() => {
    setFormData((prev) => {
      const newDueDate = (() => {
        if (!initialDueDate) return prev.dueDate;
        try {
          return moment.utc(initialDueDate).format('YYYY-MM-DD');
        } catch {
          return prev.dueDate;
        }
      })();

      // Only update if values have actually changed to avoid unnecessary re-renders
      if (
        newDueDate !== prev.dueDate ||
        (initialActionPerformed && initialActionPerformed !== prev.actionPerformed) ||
        (initialComments &&
          !isAssigneeChangeComment(initialComments) &&
          initialComments !== prev.comments)
      ) {
        return {
          ...prev,
          actionPerformed: initialActionPerformed || prev.actionPerformed,
          comments: isAssigneeChangeComment(initialComments || '')
            ? prev.comments
            : initialComments || prev.comments,
          dueDate: newDueDate,
        };
      }
      return prev;
    });
  }, [initialActionPerformed, initialComments, initialDueDate]);

  // Combined effect for media operations
  useEffect(() => {
    // Handle media deletion
    if (deleteMediaApiStatus.isSuccess && deleteMediaApiStatus.mediaId) {
      const updatedForms = { ...formData };

      if (updatedForms.mediaIdsMap) {
        const updatedMediaIdsMap = {
          ...updatedForms.mediaIdsMap,
          responseMedia:
            updatedForms.mediaIdsMap.responseMedia?.filter(
              (id) => id !== deleteMediaApiStatus.mediaId,
            ) || [],
        };
        const finalUpdatedForms = {
          ...updatedForms,
          mediaIdsMap: updatedMediaIdsMap,
        };
        if (onSave) {
          onSave(finalUpdatedForms);
          setFormData(finalUpdatedForms);
        }
      }
    }
  }, [deleteMediaApiStatus]);

  // Combined effect for media operations
  useEffect(() => {
    // Update fileItems from mediaList
    if (responseMedia && responseMedia.length > 0) {
      const items: ImageGalleryItem[] = responseMedia?.map((media) => ({
        id: media.media.mediaId,
        fileName: media.media.mediaId,
        imageUrl: media.presignedUrl,
      }));
      setFileItems(items);
    } else {
      setFileItems([]);
    }
  }, [responseMedia]);

  // Remove local isLoading state as it's now passed as prop
  const [showMarkCompleteModal, setShowMarkCompleteModal] = useState(false);
  const [showMarkInProgressModal, setShowMarkInProgressModal] = useState(false);

  const handleInputChange = (field: keyof TaskPerformanceFormData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Helper function to get max date (10 days from today)
  const getMaxDate = () => {
    return moment.utc().add(10, 'days').format('YYYY-MM-DD');
  };

  // Helper function to get min date (today)
  const getMinDate = () => {
    return moment.utc().format('YYYY-MM-DD');
  };

  // Handle file upload immediately (PerformAudit pattern)
  const handleFileUpload = async (files: File[]) => {
    if (files.length === 0) return;

    const fileData = await Promise.all(
      files.map(async (file) => {
        const base64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
        return {
          image: base64,
          extension: file.type,
        };
      }),
    );

    try {
      const uploadedMediaIds = await uploadfiles(fileData, {
        programId: user.program,
        businessUnit: userPreferences.profileSettings.businessUnit,
        entityType: EntityType.TASK,
        createdBy: user.userId,
      });

      if (uploadedMediaIds.length > 0) {
        // Update formData with new media IDs (PerformAudit pattern)
        const updatedForms = {
          ...formData,
          mediaIdsMap: {
            ...formData.mediaIdsMap,
            responseMedia: [
              ...(formData.mediaIdsMap.responseMedia || []),
              ...uploadedMediaIds,
            ],
          },
        };

        if (onSave) {
          onSave(updatedForms);
          setFormData(updatedForms);
        }
        return updatedForms;
        // Load media for display (PerformAudit pattern)
      }
    } catch (error) {
      // Error is already handled by useMediaUpload hook
      return formData;
    }

    return formData;
  };

  const handleSave = () => {
    if (isReadOnlyMode || !validate()) {
      return;
    }

    onSave(formData);
  };

  const handleStatusChange = (status: 'in-progress' | 'completed') => {
    if (isReadOnlyMode || !validate()) {
      return;
    }

    // Show confirmation modal before changing status
    if (status === 'completed') {
      setShowMarkCompleteModal(true);
    } else if (status === 'in-progress') {
      setShowMarkInProgressModal(true);
    }
  };

  const handleMarkCompleteConfirm = () => {
    // Ensure due date is in proper ISO format
    const formDataWithISODate = {
      ...formData,
      dueDate: formData.dueDate
        ? moment.utc(formData.dueDate).toISOString()
        : formData.dueDate,
    };

    onStatusChange('completed', undefined, formDataWithISODate);
    setShowMarkCompleteModal(false);
  };

  const handleMarkInProgressConfirm = () => {
    // Ensure due date is in proper ISO format
    const formDataWithISODate = {
      ...formData,
      dueDate: formData.dueDate
        ? moment.utc(formData.dueDate).toISOString()
        : formData.dueDate,
    };
    onStatusChange('in-progress', undefined, formDataWithISODate);
    setShowMarkInProgressModal(false);
  };

  // return read only mode view
  if (isReadOnlyMode) {
    return (
      <Container header={<Header variant="h3">Task Performance</Header>}>
        <SpaceBetween size="l">
          <FormField label="Auditor Observation">
            <Box padding="s" color="text-body-secondary">
              {auditorObservation || 'No auditor observation available'}
            </Box>
          </FormField>

          <FormField label="Action Performed">
            <Box padding="s" color="text-body-secondary">
              {formData.actionPerformed || '-'}
            </Box>
          </FormField>

          <FormField label="Comments">
            <Box padding="s" color="text-body-secondary">
              {formData.comments || '-'}
            </Box>
          </FormField>

          {fileItems.length > 0 && (
            <FormField label="Attachments">
              <ImageGallery
                items={fileItems}
                readOnly
                loading={false}
                onDelete={() => {}}
              />
            </FormField>
          )}
        </SpaceBetween>
      </Container>
    );
  }

  return (
    <>
      <UploadProgressIndicator
        isUploading={isUploading}
        uploadProgress={uploadProgress}
      />

      <ApiStatusFlashBar
        apiStatus={[uploadMediaApiStatus, deleteMediaApiStatus]}
        clearMessages={clearMediaAllApiStatus}
      />

      <Container header={<Header variant="h3">Task Performance</Header>}>
        <SpaceBetween size="l">
          {errorALLMessage && (
            <Alert type="error" header="Validation Error">
              {errorALLMessage}
            </Alert>
          )}

          <FormField label="Auditor Observation">
            <Box padding="s" color="text-body-secondary">
              {auditorObservation || 'No auditor observation available'}
            </Box>
          </FormField>

          <FormField
            label={
              taskStatus !== TaskStatus.ASSIGNED &&
              taskStatus !== TaskStatus.CANCELLED &&
              taskStatus !== TaskStatus.COMPLETED ? (
                <span>
                  Action Performed <i style={{ color: colorTextStatusError }}>*</i>
                </span>
              ) : (
                'Action Performed'
              )
            }
            errorText={fieldValidationError.actionPerformed}
          >
            <Textarea
              value={formData.actionPerformed}
              onChange={({ detail }) =>
                handleInputChange('actionPerformed', detail.value)
              }
              placeholder="Describe the Actions you performed"
              rows={3}
              disabled={isFieldDisabled}
              invalid={!!fieldValidationError.actionPerformed}
              data-testid="action-performed-field"
            />
          </FormField>

          <FormField label="Comments">
            <Textarea
              value={formData.comments}
              onChange={({ detail }) => handleInputChange('comments', detail.value)}
              placeholder="Add any Additional Comments"
              rows={2}
              disabled={isFieldDisabled}
              data-testid="comments-field"
            />
          </FormField>

          <FormField label="Attachments">
            <SpaceBetween size="s">
              {/* File Upload - Only show if not disabled */}
              {!isFieldDisabled && (
                <FileUpload
                  accept=".jpg,.jpeg,.png"
                  onChange={({ detail }) => handleFileUpload(detail.value)}
                  i18nStrings={{
                    uploadButtonText: (multiple: boolean) =>
                      multiple ? 'Choose files' : 'Choose file',
                    dropzoneText: (multiple: boolean) =>
                      multiple ? 'Drop files to upload' : 'Drop file to upload',
                    removeFileAriaLabel: (index: number) => `Remove file ${index + 1}`,
                    limitShowFewer: 'Show fewer files',
                    limitShowMore: 'Show more files',
                    errorIconAriaLabel: 'Error',
                  }}
                  multiple
                  showFileSize
                  showFileThumbnail
                  value={[]}
                />
              )}

              {/* Image Gallery for uploaded media */}
              <ImageGallery
                items={fileItems}
                readOnly={isFieldDisabled}
                loading={deleteMediaApiStatus.isLoading}
                onDelete={(mediaId) =>
                  deleteMedia({
                    programId: user.program,
                    deletedBy: user.userId,
                    mediaId,
                  })
                }
              />
            </SpaceBetween>
          </FormField>

          {taskStatus !== TaskStatus.CANCELLED && taskStatus !== TaskStatus.COMPLETED && (
            <SpaceBetween size="xs" direction="horizontal" alignItems="center">
              {taskStatus === TaskStatus.ASSIGNED ? (
                <Button
                  onClick={() => handleStatusChange('in-progress')}
                  variant="primary"
                  data-testid="mark-in-progress-button"
                  loading={isLoading}
                >
                  Mark In Progress
                </Button>
              ) : (
                <>
                  <Button
                    onClick={handleSave}
                    variant="normal"
                    data-testid="save-button"
                    loading={isLoading}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => handleStatusChange('completed')}
                    variant="primary"
                    data-testid="save-complete-button"
                    loading={isLoading}
                  >
                    Save & Complete
                  </Button>
                </>
              )}
            </SpaceBetween>
          )}
        </SpaceBetween>
      </Container>

      <MarkCompleteModal
        visible={showMarkCompleteModal}
        onDismiss={() => setShowMarkCompleteModal(false)}
        onConfirm={handleMarkCompleteConfirm}
      />

      <MarkInProgressModal
        visible={showMarkInProgressModal}
        onDismiss={() => setShowMarkInProgressModal(false)}
        onConfirm={handleMarkInProgressConfirm}
      />
    </>
  );
};
